// src/contexts/AuthContext.js
// Auth custom via RPC Supabase (login_user / register_user)
// Les users existants ont des mots de passe bcrypt dans la table `users` directement.
// On ne peut pas utiliser supabase.auth car ils ne sont pas dans auth.users.
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Restaurer la session depuis localStorage au montage
   */
  const checkAuth = useCallback(async (skipRedirect = false) => {
    const storedUser = localStorage.getItem('crou_user');

    if (!storedUser) {
      setIsAuthenticated(false);
      setUserInfo(null);
      setLoading(false);
      if (!skipRedirect) navigate('/auth');
      return false;
    }

    try {
      const user = JSON.parse(storedUser);
      // Vérifier que l'utilisateur existe toujours en base
      const { data, error } = await supabase
        .from('users')
        .select('id, full_name, phone, site, batiment, palier, chambre, categories, shop_name, shop_description, created_at')
        .eq('id', user.id)
        .single();

      if (error || !data) {
        localStorage.removeItem('crou_user');
        setIsAuthenticated(false);
        setUserInfo(null);
        setLoading(false);
        if (!skipRedirect) navigate('/auth');
        return false;
      }

      const profile = {
        id: data.id,
        name: data.full_name,
        phone: data.phone,
        site: data.site,
        batiment: data.batiment,
        palier: data.palier,
        chambre: data.chambre,
        categories: data.categories,
        shopName: data.shop_name,
        shopDescription: data.shop_description,
        createdAt: data.created_at,
      };

      setIsAuthenticated(true);
      setUserInfo(profile);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('[Auth] Erreur checkAuth:', err);
      localStorage.removeItem('crou_user');
      setIsAuthenticated(false);
      setUserInfo(null);
      setLoading(false);
      if (!skipRedirect) navigate('/auth');
      return false;
    }
  }, [navigate]);

  /**
   * Connexion via RPC login_user (bcrypt côté serveur)
   */
  const login = useCallback(async ({ phone, password }) => {
    try {
      const { data, error } = await supabase.rpc('login_user', {
        p_phone: phone,
        p_password: password,
      });

      if (error) {
        console.error('[Auth] RPC error:', error);
        return { success: false, error: 'Erreur de connexion.' };
      }

      if (!data.success) {
        return { success: false, error: data.error };
      }

      const user = data.user;
      localStorage.setItem('crou_user', JSON.stringify(user));
      setIsAuthenticated(true);
      setUserInfo(user);
      return { success: true, user };
    } catch (err) {
      console.error('[Auth] Erreur login:', err);
      return { success: false, error: 'Erreur de connexion.' };
    }
  }, []);

  /**
   * Inscription via RPC register_user (bcrypt côté serveur)
   */
  const register = useCallback(async (formData) => {
    try {
      const { data, error } = await supabase.rpc('register_user', {
        p_name: formData.name,
        p_phone: formData.phone,
        p_password: formData.password,
        p_site: formData.site || null,
        p_batiment: formData.batiment || null,
        p_palier: formData.palier || null,
        p_chambre: formData.chambre || null,
        p_categories: formData.categories || [],
        p_shop_name: formData.shopName || null,
        p_shop_description: formData.shopDescription || null,
      });

      if (error) {
        console.error('[Auth] RPC error:', error);
        return { success: false, error: 'Erreur lors de l\'inscription.' };
      }

      if (!data.success) {
        return { success: false, error: data.error };
      }

      const user = data.user;
      localStorage.setItem('crou_user', JSON.stringify(user));
      setIsAuthenticated(true);
      setUserInfo(user);
      return { success: true, user };
    } catch (err) {
      console.error('[Auth] Erreur register:', err);
      return { success: false, error: 'Erreur lors de l\'inscription.' };
    }
  }, []);

  /**
   * Déconnexion
   */
  const logout = useCallback(() => {
    localStorage.removeItem('crou_user');
    setIsAuthenticated(false);
    setUserInfo(null);
    navigate('/auth');
  }, [navigate]);

  // Vérifier au montage
  useEffect(() => {
    checkAuth(true);
  }, [checkAuth]);

  const value = {
    isAuthenticated,
    userInfo,
    loading,
    checkAuth,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
}